import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';

import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';

import { Movement, Transaction } from 'types';
import { getNewOperationDate } from 'utils/getNewOperationDate';
import { RenumerationModalContextType } from '../types/RenumerationModalContextType';
import { renumerationModalInitValue } from '../constants/renumerationModalInitValue';

const RenumerationModalContext = createContext<RenumerationModalContextType>(
  renumerationModalInitValue
);

export const RenumerationModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const [scriptureView, setScriptureView] = useState<boolean>(true);

  const [date, setDate] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const onChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const onChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const onChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleSaveRenumeration = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);
        const newMovements: Movement[] = [];
        const newTransactions: Transaction[] = [];

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: file.name,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const renumerationData = {
          movements: newMovements,
          transactions: newTransactions,
          date: newOperationDate,
          nominalValue: actualSociety.nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          notarialRegistration: {
            notaryName,
            notaryCity,
            protocolNr: notaryProtocol,
          },
          operationType: operationTypes.RENUMERATION,
        };

        await store.dispatch(addOperation(renumerationData));
        setIsLoading(false);

        successSwal(t('RenumerationSuccessfullyCompleted'));
      } catch (error) {
        errorSwal(t('ErrorWhenPerformingRenumeration'));
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <RenumerationModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          scriptureView,
          lastOperationDate: formattedLastOperationDate,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          onChangeNotaryName,
          onChangeNotaryProtocol,
          onChangeNotaryCity,
          handleSaveRenumeration,
        },
      }}
    >
      {children}
    </RenumerationModalContext.Provider>
  );
};

export const useRenumerationModal = () => {
  const context = useContext(RenumerationModalContext);
  if (!context) {
    throw new Error(
      'useRenumeration must be used within a RenumerationModalProvider'
    );
  }
  return context;
};
